import React, { useEffect, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import AiGenerativeRemoveIcon from '@atlaskit/icon-lab/core/ai-generative-remove';

import { useAISmartButtonActions } from '@atlassian/ai-smart-button-api';
import {
	type ConversationAction,
	HandleActionType,
	usePromptCards,
} from '@atlassian/rovo-conversation-actions';

import { CommentCountsProvider } from '@confluence/comment-context/entry-points/CommentCountsContext.ts';
import { expVal } from '@confluence/feature-experiments';
import { useKeepPageStructure } from '@confluence/experiment-copy-with-rovo/entry-points/useKeepPageStructure.ts';
import { isRecentlyCopiedContentId } from '@confluence/experiment-copy-with-rovo/entry-points/recentlyCopiedContent.ts';
import { useViewPageRovoActions } from '@confluence/page-rovo-config-common/entry-points/useViewPageRovoActions.ts';
import {
	type RovoConfiguration,
	type RovoConfigurationProviderProps,
	type PageRovoConfigProviderProps,
	isPageRovoConfigProviderProps,
} from '@confluence/rovo-config-provider-common/entry-points/rovoConfigTypes.ts';
import { useRovoConfigProviderProps } from '@confluence/rovo-config-provider-common/entry-points/useRovoConfigProviderProps.ts';

import { useLiveDocNudgeRecommendationFilter } from './hooks/useLiveDocNudgeRecommendationFilter.ts';
import {
	useEditorRovoButtonActionsActions,
	useEditorRovoButtonActionsContext,
} from './store/EditorRovoButtonActionsStore.tsx';
import { getEmptyPageCreationActions } from './utils/getEmptyPageCreationActions.tsx';
import emptyPageGreetingIcon from './assets/empty-page-greeting-icon.svg';

const messages = defineMessages({
	emptyPageGreetingTitle: {
		id: 'edit-page-rovo-config-provider.empty-page-greeting-title',
		defaultMessage: 'Start your doc with a prompt',
		description: 'Greeting title shown in the Rovo empty state when the editor page is empty',
	},
	keepPageStructureTitle: {
		id: 'edit-page-rovo-config-provider.keep-page-structure-title',
		defaultMessage: 'Remove content, keep structure',
		description:
			'Title for the Rovo action that strips user-specific content while preserving page structure',
	},
	keepPageStructureSecondaryTitle: {
		id: 'edit-page-rovo-config-provider.keep-page-structure-secondary-title',
		defaultMessage: 'Strip user content while preserving page structure',
		description:
			'Secondary description for the Rovo action that strips user-specific content while preserving page structure',
	},
});

const EditPageConfigProviderInner = ({ setConfiguration }: RovoConfigurationProviderProps) => {
	const viewPageRovoActions = useViewPageRovoActions();
	const editorActions = useEditorRovoButtonActionsActions();
	const editorActionsContext = useEditorRovoButtonActionsContext();
	const rovoConfigProviderProps = useRovoConfigProviderProps();
	const isEditorEmpty =
		isPageRovoConfigProviderProps(rovoConfigProviderProps) && rovoConfigProviderProps.isEditorEmpty;
	const { formatMessage } = useIntl();

	const { promptCards } = usePromptCards();

	const isEmptyPageWithPromptCards = isEditorEmpty && Array.isArray(promptCards);

	const pageProps = isPageRovoConfigProviderProps(rovoConfigProviderProps)
		? rovoConfigProviderProps
		: null;

	const contentType = pageProps?.contentType ?? '';
	const contentId = pageProps?.contentId ?? '';

	const enableLiveDocNudges = useLiveDocNudgeRecommendationFilter(contentType, contentId);
	const { setNudges } = useAISmartButtonActions();

	const shouldShowKeepStructure =
		expVal('cc_fd_copy_with_rovo_gate', 'isEnabled', false) && isRecentlyCopiedContentId(contentId);

	const { keepPageStructure } = useKeepPageStructure({
		contentId,
		enabled: shouldShowKeepStructure,
	});

	const keepPageStructureAction: ConversationAction = useMemo(
		() => ({
			id: 'keep-page-structure-ai-action',
			title: formatMessage(messages.keepPageStructureTitle),
			secondaryTitle: formatMessage(messages.keepPageStructureSecondaryTitle),
			icon: () => <AiGenerativeRemoveIcon label="" />,
			type: 'ai-action',
			handleType: HandleActionType.Base,
			onClick: keepPageStructure,
		}),
		[keepPageStructure, formatMessage],
	);

	const config = useMemo<RovoConfiguration>(() => {
		return {
			getActions: async () => {
				if (isEmptyPageWithPromptCards) {
					return Promise.resolve(getEmptyPageCreationActions(promptCards));
				}

				if (editorActionsContext?.reference) {
					const actions = shouldShowKeepStructure
						? [keepPageStructureAction, ...editorActions]
						: editorActions;
					return Promise.resolve(actions);
				}

				// Find create-new-content action
				const createNewContentAction = viewPageRovoActions.find(
					(action: { actionId: string }) => action.actionId === 'create-new-content',
				);

				// If create-new-content action exists, reposition it to 4th item, last visible before show more
				if (createNewContentAction) {
					// Filter out create-new-content from view page actions
					// Small bounded array (typically <10 actions), no risk of quadratic performance issues.
					// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
					const viewPageActionsWithoutCreate = viewPageRovoActions
						.filter((action: { actionId: string }) => action.actionId !== 'create-new-content')
						.map((action: { action: ConversationAction }) => action.action);

					// Build final actions array
					const combinedActions = [
						...(shouldShowKeepStructure ? [keepPageStructureAction] : []),
						...editorActions,
						...viewPageActionsWithoutCreate,
					];

					// Insert create-new-content at position index 3 (4th position), or at the end if there are less than 3 actions
					const insertIndex = Math.min(3, combinedActions.length);
					combinedActions.splice(insertIndex, 0, createNewContentAction.action);

					return Promise.resolve(combinedActions);
				}

				// Fallback: no create-new-content action, use original behavior
				const viewPageActions = viewPageRovoActions.map(
					(action: { action: ConversationAction }) => action.action,
				);

				const allActions = [
					...(shouldShowKeepStructure ? [keepPageStructureAction] : []),
					...editorActions,
					...viewPageActions,
				];
				return Promise.resolve(allActions);
			},
			customGreeting: isEmptyPageWithPromptCards
				? {
						iconSrc: emptyPageGreetingIcon,
						title: formatMessage(messages.emptyPageGreetingTitle),
					}
				: undefined,
			shouldShowGreeting: isEmptyPageWithPromptCards ? true : undefined,
			prioritizeCustomActionResults:
				isEmptyPageWithPromptCards || shouldShowKeepStructure ? true : undefined,
		};
	}, [
		editorActions,
		editorActionsContext,
		viewPageRovoActions,
		isEmptyPageWithPromptCards,
		promptCards,
		formatMessage,
		shouldShowKeepStructure,
		keepPageStructureAction,
	]);

	useEffect(() => {
		setConfiguration(config);

		if (!enableLiveDocNudges) {
			setNudges([]);
			return;
		}

		const nudges = [
			viewPageRovoActions.find((a: { actionId: string }) => a.actionId === 'audio-briefing')
				?.action,
		].filter(Boolean) as ConversationAction[];

		if (nudges.length > 0) {
			setNudges(nudges);
		}
	}, [
		config,
		setConfiguration,
		enableLiveDocNudges,
		viewPageRovoActions,
		setNudges,
		contentType,
		contentId,
	]);

	return null;
};

export const EditPageRovoConfigProvider = (props: RovoConfigurationProviderProps) => {
	const { contentId } = useRovoConfigProviderProps() as PageRovoConfigProviderProps;
	return (
		<CommentCountsProvider contentId={contentId}>
			<EditPageConfigProviderInner {...props} />
		</CommentCountsProvider>
	);
};
